import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useContext, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Add, ArrowBack, ChevronRight, Search } from '@mui/icons-material';
import { Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, List, ListItem, ListItemButton, ListItemText, Stack, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';

import { CreateDomainStoryRequest, PlanningClient } from '@@api';
import { useApiClient, useToastService } from '@@common';
import { ViewTitleBar } from '@@components';

import { formatTimestamp } from '../../common/formatTimestamp';
import AuthContext from '../../components/AuthContext';
import PlanningChatPanel from './PlanningChatPanel';
import { usePlanningChatAdapter } from './usePlanningChatAdapter';

export default function PlanningProjectDetailView() {
	const { projectId } = useParams();
	const navigate = useNavigate();
	const client = useApiClient(PlanningClient);
	const queryClient = useQueryClient();
	const toast = useToastService();
	const auth = useContext(AuthContext);
	const [activeTab, setActiveTab] = useState<'stories' | 'actors'>('stories');
	const [searchText, setSearchText] = useState('');
	const [createStoryOpen, setCreateStoryOpen] = useState(false);
	const [storyTitle, setStoryTitle] = useState('');
	const [storyDescription, setStoryDescription] = useState('');
	const chatAdapter = usePlanningChatAdapter(projectId, undefined, auth.isAuthenticated);

	const projectQuery = useQuery({
		queryKey: ['planning', 'project', projectId],
		queryFn: () => client.getProject(projectId!),
		enabled: !!projectId,
	});

	const storiesQuery = useQuery({
		queryKey: ['planning', 'stories', projectId, searchText],
		queryFn: () => client.searchDomainStories(projectId!, searchText.trim() || undefined, undefined, 0, 100),
		enabled: !!projectId && activeTab === 'stories',
	});

	const actorsQuery = useQuery({
		queryKey: ['planning', 'actors', searchText],
		queryFn: () => client.searchActors(searchText.trim() || undefined, undefined, undefined),
		enabled: activeTab === 'actors',
	});

	const activeMutation = useMutation({
		mutationFn: async () => {
			if (projectQuery.data?.isActive) await client.deactivateProject(projectId!);
			else await client.activateProject(projectId!);
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ['planning', 'project', projectId] }),
		onError: (error) => toast.error(error instanceof Error ? error.message : 'Failed to update project.'),
	});

	const createStoryMutation = useMutation({
		mutationFn: () => {
			const request: CreateDomainStoryRequest = {
				name: storyTitle.trim(),
				description: storyDescription.trim(),
			};
			return client.createDomainStory(projectId!, request);
		},
		onSuccess: (result) => {
			setCreateStoryOpen(false);
			setStoryTitle('');
			setStoryDescription('');
			queryClient.invalidateQueries({ queryKey: ['planning', 'stories', projectId] });
			if (result.id) {
				navigate(`/planning/projects/${projectId}/stories/${result.id}`);
			}
		},
		onError: (error) => toast.error(error instanceof Error ? error.message : 'Failed to create domain story.'),
	});

	const refreshPlanning = async () => {
		await queryClient.invalidateQueries({ queryKey: ['planning'] });
	};

	if (projectQuery.isLoading) {
		return <Box sx={{ display: 'grid', placeItems: 'center', flex: 1 }}><CircularProgress size={28} /></Box>;
	}

	if (!projectQuery.data) {
		return <Box sx={{ p: 3 }}><Typography>Planning project not found.</Typography></Box>;
	}

	const project = projectQuery.data;
	return (
		<Stack sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
			<ViewTitleBar title={project.name}>
				<Stack direction="row" spacing={1} alignItems="center">
					<Chip label={project.isActive ? 'Active' : 'Inactive'} color={project.isActive ? 'success' : 'default'} size="small" variant="outlined" />
					<Button size="small" onClick={() => activeMutation.mutate()} disabled={activeMutation.isPending}>
						{project.isActive ? 'Deactivate' : 'Activate'}
					</Button>
				</Stack>
			</ViewTitleBar>

			<Stack direction={{ xs: 'column', md: 'row' }} sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
				<PlanningChatPanel adapter={chatAdapter} onComplete={refreshPlanning} />
				<Stack sx={{ order: { xs: -1, md: 0 }, flex: 1, minWidth: 0, minHeight: { xs: 480, md: 0 } }}>
					<Box sx={{ px: { xs: 1.5, md: 2.5 }, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
						<Stack direction="row" alignItems="center" spacing={1}>
							<Tooltip title="Back to projects"><IconButton size="small" onClick={() => navigate('/planning/projects')}><ArrowBack /></IconButton></Tooltip>
							<Box sx={{ minWidth: 0 }}>
								<Typography variant="body2" color="text.secondary">Planning project</Typography>
								<Typography variant="body1">{project.description || 'No description'}</Typography>
							</Box>
						</Stack>
					</Box>

					<Box sx={{ px: { xs: 1.5, md: 2.5 }, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
						<Tabs
							value={activeTab}
							onChange={(_, value: 'stories' | 'actors') => {
								setActiveTab(value);
								setSearchText('');
							}}
						>
							<Tab label="Domain Stories" value="stories" />
							<Tab label="Actors" value="actors" />
						</Tabs>
					</Box>

					<Box sx={{ px: { xs: 1.5, md: 2.5 }, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
						<TextField
							size="small"
							label={activeTab === 'stories' ? 'Search domain stories' : 'Search actors'}
							value={searchText}
							onChange={(event) => setSearchText(event.target.value)}
							sx={{ width: { xs: '100%', sm: 360 } }}
							slotProps={{ input: { startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> } }}
						/>
					</Box>

					<Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
						{activeTab === 'stories' ? (
							<>
								<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, pt: 2, pb: 0.5 }}>
									<Typography variant="subtitle2">Domain Stories</Typography>
									<Button size="small" startIcon={<Add />} onClick={() => setCreateStoryOpen(true)}>Create Domain Story</Button>
								</Stack>
								{storiesQuery.isLoading ? (
									<Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress size={24} /></Box>
								) : storiesQuery.data?.items.length ? (
									<List disablePadding>
										{storiesQuery.data.items.map((story) => (
											<ListItemButton key={story.id} divider onClick={() => navigate(`/planning/projects/${projectId}/stories/${story.id}`)} sx={{ px: 2.5, py: 1.5 }}>
												<ListItemText
													primary={story.title}
											secondary={`Revision ${story.currentRevisionNumber} · Updated ${formatTimestamp(story.updatedAt, true)}`}
												/>
												<Chip label={story.status} size="small" variant="outlined" color={story.status === 'Accepted' ? 'success' : story.status === 'InReview' ? 'info' : 'default'} />
												<ChevronRight color="action" sx={{ ml: 1 }} />
											</ListItemButton>
										))}
									</List>
								) : (
									<Box sx={{ px: 2.5, py: 5, textAlign: 'center' }}>
										<Typography variant="body2" color="text.secondary">No domain stories found.</Typography>
									</Box>
								)}
							</>
						) : actorsQuery.isLoading ? (
							<Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress size={24} /></Box>
						) : actorsQuery.data?.length ? (
							<List disablePadding>
								{actorsQuery.data.map((actor) => (
									<ListItem key={actor.id} divider sx={{ px: 2.5, py: 1.5 }}>
										<ListItemText
											primary={actor.name}
											secondary={`${actor.actorType}${actor.aliases.length ? ` · Also known as ${actor.aliases.join(', ')}` : ''} · Updated ${formatTimestamp(actor.updatedAt, true)}`}
										/>
										<Chip label={actor.status} size="small" variant="outlined" color={actor.status === 'Active' ? 'success' : 'default'} />
									</ListItem>
								))}
							</List>
						) : (
							<Box sx={{ px: 2.5, py: 5, textAlign: 'center' }}>
								<Typography variant="body2" color="text.secondary">No actors found.</Typography>
							</Box>
						)}
					</Box>
				</Stack>
			</Stack>

			<Dialog open={createStoryOpen} onClose={() => setCreateStoryOpen(false)} fullWidth maxWidth="sm">
				<DialogTitle>Create Domain Story</DialogTitle>
				<DialogContent dividers>
					<Stack spacing={2} sx={{ pt: 0.5 }}>
						<TextField autoFocus required label="Title" value={storyTitle} onChange={(event) => setStoryTitle(event.target.value)} />
						<TextField label="Description" value={storyDescription} onChange={(event) => setStoryDescription(event.target.value)} multiline minRows={4} />
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setCreateStoryOpen(false)}>Cancel</Button>
					<Button variant="contained" onClick={() => createStoryMutation.mutate()} disabled={!storyTitle.trim() || createStoryMutation.isPending}>Create</Button>
				</DialogActions>
			</Dialog>
		</Stack>
	);
}
